import { useEffect, useState } from "react";
import { Clock, CheckCircle2, AlertTriangle, Circle, FileText } from "lucide-react";
import { api, formatDateTime } from "@/lib/ocorenApi";

const iconFor = (mov) => {
  const desc = (mov.descricao_ocorrencia || "").toUpperCase();
  const code = mov.codigo_ocorrencia || "";
  if (desc.includes("ENTREGA REALIZADA"))
    return { Icon: CheckCircle2, color: "#10B981" };
  if (
    /AVARIA|EXTRAVIO|RECUSAD|ROUBO|DEVOLU|NAO LOCALIZAD|AUSENTE|FECHAD/.test(desc) ||
    ["04", "05", "06", "08", "09", "10", "11", "26"].includes(code)
  )
    return { Icon: AlertTriangle, color: "#F43F5E" };
  return { Icon: Circle, color: "#3B82F6" };
};

export const NFTimeline = ({ numero_nota, cnpj_cliente }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!numero_nota) return;
    setLoading(true);
    const params = { numero_nota };
    if (cnpj_cliente) params.cnpj_cliente = cnpj_cliente;
    api
      .get("/ocoren/movimentacoes/timeline", { params })
      .then((res) => setItems(res.data?.items || []))
      .finally(() => setLoading(false));
  }, [numero_nota, cnpj_cliente]);

  // Deduplica eventos idênticos (mesma data + código + descrição + arquivo) — comum quando
  // a mesma ocorrência aparece em múltiplas linhas do mesmo arquivo. Eventos do mesmo
  // momento vindos de arquivos diferentes são preservados (atualizações reais de status).
  const dedupedItems = [];
  const seen = new Set();
  for (const m of items) {
    const key = `${m.data_ocorrencia}|${m.codigo_ocorrencia}|${m.descricao_ocorrencia}|${m.nome_arquivo}`;
    if (seen.has(key)) continue;
    seen.add(key);
    dedupedItems.push(m);
  }

  if (loading)
    return (
      <div className="text-xs text-[#52525B] py-4" data-testid="nf-timeline-loading">
        Carregando histórico…
      </div>
    );

  if (dedupedItems.length === 0)
    return (
      <div className="text-xs text-[#52525B] py-4">
        Nenhum evento adicional encontrado para esta NF.
      </div>
    );

  return (
    <div data-testid="nf-timeline" className="relative">
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#52525B] font-bold mb-3 flex items-center gap-1.5">
        <Clock size={12} />
        Linha do Tempo ({dedupedItems.length} evento(s){items.length !== dedupedItems.length ? `, ${items.length - dedupedItems.length} duplicados ocultos` : ""})
      </div>

      <ol className="relative border-l border-[#E4E4E7] ml-2 space-y-4 pb-1">
        {dedupedItems.map((m, idx) => {
          const { Icon, color } = iconFor(m);
          return (
            <li
              key={m.id || idx}
              data-testid={`nf-timeline-item-${idx}`}
              className="ml-4 relative"
            >
              <span
                className="absolute -left-[22px] top-0 w-3 h-3 rounded-full bg-white border-2 flex items-center justify-center"
                style={{ borderColor: color }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: color }}
                />
              </span>

              <div className="flex items-center gap-1.5 text-[11px] font-mono-data text-[#52525B]">
                <Icon size={12} style={{ color }} />
                {formatDateTime(m.data_ocorrencia)}
                <span className="text-[#A1A1AA]">•</span>
                <span className="font-semibold">{m.codigo_ocorrencia || "—"}</span>
              </div>
              <div className="text-sm text-[#09090B] mt-1 flex items-center gap-2">
                <span>{m.descricao_ocorrencia || "—"}</span>
                {m.ocorrencias > 1 && (
                  <span
                    data-testid={`nf-timeline-repeat-${idx}`}
                    title={`Este status se repetiu ${m.ocorrencias} vezes`}
                    className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded-sm bg-[#FAFAFA] text-[#52525B] border border-[#E4E4E7]"
                  >
                    {m.ocorrencias}x
                  </span>
                )}
              </div>
              <div className="text-[11px] text-[#52525B] mt-1 flex items-center gap-1">
                {m.origem === "manual" ? (
                  <span
                    data-testid={`nf-timeline-manual-${idx}`}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-violet-50 text-violet-700 border border-violet-200 font-semibold"
                  >
                    Manual
                  </span>
                ) : (
                  <>
                    <FileText size={10} />
                    {m.nome_arquivo}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default NFTimeline;
